/**
 * TTSSettingsPanel — Phase 16 TTS provider configuration UI.
 *
 * Renders inline inside the AI Coach Narrator section of Settings. Shows
 * provider radios (browser / OpenAI / ElevenLabs), then a provider-specific
 * sub-panel for keys + model + per-personality voice map + Test button.
 *
 * Stateless: takes `settings` (the full coachLLM object) + `onChange`
 * (the same setter used by the parent). Doesn't own any state — the
 * parent's `setCoachLLMSettings` is the source of truth.
 *
 * Props:
 *   settings   coachLLMSettings — full slice; we read settings.tts + voice
 *   onChange   (prev) => updated  — same setter pattern as the parent
 *   darkMode
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Volume2, AlertCircle, Check } from 'lucide-react';
import {
  speak,
  stop,
  listBrowserVoices,
  TTS_PROVIDERS,
  isProviderReady,
} from '../../utils/coachTTS';
import { OPENAI_VOICES, OPENAI_MODELS, OPENAI_DEFAULT_VOICE_MAP } from '../../utils/tts/openaiTTS';
import GlassSelect from '../Common/GlassSelect';
import {
  ELEVENLABS_DEFAULT_VOICE_MAP,
  ELEVENLABS_VOICE_NAMES,
  ELEVENLABS_MODELS,
} from '../../utils/tts/elevenlabsTTS';

const PERSONALITIES = ['mentor', 'analyst', 'drill', 'friend'];

// Helper: update a nested key inside settings.tts via the parent setter.
// Without this, every input handler would need its own deep-merge dance.
const useTtsUpdater = (onChange) => {
  return (patch) => {
    onChange(prev => ({
      ...prev,
      tts: { ...(prev.tts || {}), ...patch },
    }));
  };
};

const PROVIDER_META = {
  browser: {
    label: 'Browser (free)',
    desc: 'Uses your OS / browser voices. Quality varies — Google voices in Chromium are decent, Windows default voices are robotic.',
    cost: 'No added cost',
  },
  openai: {
    label: 'OpenAI TTS',
    desc: 'Six natural-sounding voices via OpenAI\'s audio API. Very natural, very cheap.',
    cost: '~$0.005–$0.015 per Coach paragraph',
  },
  elevenlabs: {
    label: 'ElevenLabs',
    desc: 'Best-in-class voice quality, near-indistinguishable from human. Free tier covers ~20–30 paragraphs/month.',
    cost: '~$0.02–$0.05 per Coach paragraph',
  },
};

const TestButton = ({ provider, settings, personality, darkMode }) => {
  const [state, setState] = useState('idle');  // 'idle' | 'playing' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const ready = isProviderReady(provider, settings.tts || {});

  // Reset state when provider switches so a previous error doesn't linger.
  useEffect(() => { setState('idle'); setErrorMsg(''); }, [provider]);

  const onTest = async () => {
    if (state === 'playing') {
      stop();
      setState('idle');
      return;
    }
    const sample = `Hello — this is the ${personality} voice. If you can hear this, your text-to-speech is working.`;
    setState('playing');
    setErrorMsg('');
    const res = await speak(sample, {
      voice: personality,
      ttsSettings: settings.tts,
    });
    if (res?.ok) {
      setState('idle');
    } else {
      setState('error');
      setErrorMsg(res?.error || 'Failed');
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onTest}
        disabled={!ready && state !== 'playing'}
        className={`text-[11px] px-2.5 py-1 rounded-md border inline-flex items-center gap-1.5 transition-colors ${
          !ready
            ? (darkMode ? 'bg-gray-800/40 border-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed')
            : state === 'playing'
              ? (darkMode ? 'bg-purple-500/20 border-purple-500/60 text-purple-200' : 'bg-purple-100 border-purple-400 text-purple-800')
              : (darkMode ? 'bg-gray-800/60 border-gray-700 text-gray-200 hover:border-purple-500/50' : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400')
        }`}
        title={!ready ? 'Add an API key to test' : (state === 'playing' ? 'Stop' : `Test ${personality} voice`)}
      >
        {state === 'playing' ? <Loader2 size={11} className="animate-spin" /> : <Volume2 size={11} />}
        {state === 'playing' ? 'Stop' : 'Test'}
      </button>
      {state === 'error' && (
        <span className={`text-[11px] inline-flex items-center gap-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`} title={errorMsg}>
          <AlertCircle size={11} /> {errorMsg.slice(0, 60)}
        </span>
      )}
    </div>
  );
};

const TTSSettingsPanel = ({ settings, onChange, darkMode = true }) => {
  const tts = settings.tts || {};
  const provider = TTS_PROVIDERS.includes(tts.provider) ? tts.provider : 'browser';
  const updateTts = useTtsUpdater(onChange);

  // Browser voice list — refetched on mount AND when voices change (some
  // hosts populate the list async). Without the voiceschanged hook, the
  // dropdown would show empty on first paint of Settings.
  const [browserVoices, setBrowserVoices] = useState(() => listBrowserVoices());
  useEffect(() => {
    setBrowserVoices(listBrowserVoices());
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      const handler = () => setBrowserVoices(listBrowserVoices());
      window.speechSynthesis.addEventListener?.('voiceschanged', handler);
      return () => window.speechSynthesis.removeEventListener?.('voiceschanged', handler);
    }
    return undefined;
  }, []);

  const sortedBrowserVoices = useMemo(() => {
    // Sort by quality hint then name so the best options bubble to the top
    const rank = { High: 0, Good: 1, Standard: 2 };
    return [...browserVoices].sort((a, b) =>
      (rank[a.qualityHint] ?? 9) - (rank[b.qualityHint] ?? 9) ||
      a.name.localeCompare(b.name)
    );
  }, [browserVoices]);

  const inputCls = darkMode
    ? 'bg-gray-800/60 border-gray-700 text-gray-100 focus:border-purple-500/60 focus:ring-purple-500/30'
    : 'bg-white border-gray-300 text-gray-900 focus:border-purple-500 focus:ring-purple-300/50';
  const labelCls = darkMode ? 'text-gray-300' : 'text-gray-700';
  const helpCls = darkMode ? 'text-gray-500' : 'text-gray-500';

  return (
    <div className="mt-4 space-y-3">
      {/* Provider radios — three small pill buttons. */}
      <div>
        <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1.5 ${labelCls}`}>
          TTS Provider
        </div>
        <div className="grid grid-cols-3 gap-2">
          {TTS_PROVIDERS.map(p => {
            const active = provider === p;
            const meta = PROVIDER_META[p];
            return (
              <button
                key={p}
                type="button"
                onClick={() => updateTts({ provider: p })}
                className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                  active
                    ? (darkMode
                      ? 'bg-purple-500/20 border-purple-500/60 ring-1 ring-purple-500/40'
                      : 'bg-purple-100 border-purple-500 ring-1 ring-purple-300')
                    : (darkMode
                      ? 'bg-gray-800/40 border-gray-700 hover:border-gray-600'
                      : 'bg-white border-gray-300 hover:border-gray-400')
                }`}
              >
                <div className={`text-xs font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  {meta.label}
                </div>
                <div className={`text-[10px] mt-0.5 ${helpCls}`}>
                  {meta.cost}
                </div>
              </button>
            );
          })}
        </div>
        <p className={`text-[11px] mt-1.5 italic ${helpCls}`}>
          {PROVIDER_META[provider].desc}
        </p>
      </div>

      {/* Browser provider config */}
      {provider === 'browser' && (
        <div className="space-y-2">
          <div>
            <label className={`block text-[11px] uppercase tracking-wider font-semibold mb-1 ${labelCls}`}>
              Voice (auto-picks the best available if unset)
            </label>
            <GlassSelect
              value={tts.browserVoiceURI || ''}
              onChange={(v) => updateTts({ browserVoiceURI: v })}
              options={[
                { value: '', label: 'Auto-pick (recommended)' },
                ...sortedBrowserVoices.map(v => ({
                  value: v.voiceURI,
                  label: `[${v.qualityHint}] ${v.name} (${v.lang})`,
                })),
              ]}
              darkMode={darkMode}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <TestButton provider="browser" settings={settings} personality={settings.voice || 'mentor'} darkMode={darkMode} />
            <span className={`text-[11px] italic ${helpCls}`}>Plays a sample using your current personality.</span>
          </div>
        </div>
      )}

      {/* OpenAI provider config */}
      {provider === 'openai' && (
        <div className="space-y-3">
          <div>
            <label className={`block text-[11px] uppercase tracking-wider font-semibold mb-1 ${labelCls}`}>
              OpenAI API Key
            </label>
            <input
              type="password"
              value={tts.openaiApiKey || ''}
              onChange={(e) => updateTts({ openaiApiKey: e.target.value })}
              placeholder="sk-..."
              className="w-full input-modern font-mono"
              autoComplete="off"
            />
            <p className={`text-[11px] mt-1 ${helpCls}`}>
              Get a key at <span className="underline">platform.openai.com/api-keys</span>. Stored in your local settings.json; never sent to anyone but OpenAI.
            </p>
          </div>
          <div>
            <label className={`block text-[11px] uppercase tracking-wider font-semibold mb-1 ${labelCls}`}>
              Model
            </label>
            <GlassSelect
              value={tts.openaiModel || 'tts-1'}
              onChange={(v) => updateTts({ openaiModel: v })}
              options={OPENAI_MODELS.map(m => ({
                value: m,
                label: `${m} ${m === 'tts-1' ? '— fast, $15/1M chars' : '— higher quality, $30/1M chars'}`,
              }))}
              darkMode={darkMode}
            />
          </div>
          <div>
            <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1.5 ${labelCls}`}>
              Voice per personality
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PERSONALITIES.map(p => {
                const map = tts.openaiVoiceMap || OPENAI_DEFAULT_VOICE_MAP;
                const current = map[p] || OPENAI_DEFAULT_VOICE_MAP[p];
                return (
                  <div key={p} className="flex items-center gap-2">
                    <span className={`text-xs capitalize w-16 ${labelCls}`}>{p}</span>
                    <div className="flex-1">
                      <GlassSelect
                        value={current}
                        onChange={(v) => updateTts({
                          openaiVoiceMap: { ...(tts.openaiVoiceMap || OPENAI_DEFAULT_VOICE_MAP), [p]: v },
                        })}
                        options={OPENAI_VOICES}
                        darkMode={darkMode}
                        size="sm"
                      />
                    </div>
                    <TestButton provider="openai" settings={settings} personality={p} darkMode={darkMode} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ElevenLabs provider config */}
      {provider === 'elevenlabs' && (
        <div className="space-y-3">
          <div>
            <label className={`block text-[11px] uppercase tracking-wider font-semibold mb-1 ${labelCls}`}>
              ElevenLabs API Key
            </label>
            <input
              type="password"
              value={tts.elevenlabsApiKey || ''}
              onChange={(e) => updateTts({ elevenlabsApiKey: e.target.value })}
              placeholder="sk_..."
              className="w-full input-modern font-mono"
              autoComplete="off"
            />
            <p className={`text-[11px] mt-1 ${helpCls}`}>
              Get a key at <span className="underline">elevenlabs.io/app/settings/api-keys</span>. Free tier: 10k characters/month.
            </p>
          </div>
          <div>
            <label className={`block text-[11px] uppercase tracking-wider font-semibold mb-1 ${labelCls}`}>
              Model
            </label>
            <GlassSelect
              value={tts.elevenlabsModel || 'eleven_turbo_v2_5'}
              onChange={(v) => updateTts({ elevenlabsModel: v })}
              options={ELEVENLABS_MODELS}
              darkMode={darkMode}
            />
          </div>
          <div>
            <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1.5 ${labelCls}`}>
              Voice per personality
            </div>
            <div className="grid grid-cols-1 gap-2">
              {PERSONALITIES.map(p => {
                const map = tts.elevenlabsVoiceMap || ELEVENLABS_DEFAULT_VOICE_MAP;
                const current = map[p] || ELEVENLABS_DEFAULT_VOICE_MAP[p];
                return (
                  <div key={p} className="flex items-center gap-2">
                    <span className={`text-xs capitalize w-16 ${labelCls}`}>{p}</span>
                    <div className="flex-1">
                      <GlassSelect
                        value={current}
                        onChange={(v) => updateTts({
                          elevenlabsVoiceMap: { ...(tts.elevenlabsVoiceMap || ELEVENLABS_DEFAULT_VOICE_MAP), [p]: v },
                        })}
                        options={[
                          // Known catalog voices first, then a free-text fallback so
                          // the user can paste their own custom voice ID.
                          ...Object.entries(ELEVENLABS_VOICE_NAMES).map(([id, name]) => ({ value: id, label: name })),
                          ...(!ELEVENLABS_VOICE_NAMES[current] ? [{ value: current, label: `${current} (custom)` }] : []),
                        ]}
                        darkMode={darkMode}
                        size="sm"
                      />
                    </div>
                    <TestButton provider="elevenlabs" settings={settings} personality={p} darkMode={darkMode} />
                  </div>
                );
              })}
            </div>
            <p className={`text-[11px] mt-1.5 ${helpCls}`}>
              <Check size={10} className="inline mr-1" />
              You can also paste a custom voice ID from your ElevenLabs library by editing settings.json directly.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TTSSettingsPanel;
